import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
} from '@mui/material';
import { Check, Edit } from '@mui/icons-material';
import { isDockUnavailableForMovement } from '../../utils/dock';
import DestinationFacilityFilter from 'components/Campus/DestinationFacilityFilter';
import { ModalDockDoor, useModalDestinations } from 'hooks/useModalDestinations';

interface MovementTaskCompletionModalProps {
  open: boolean;
  onClose: () => void;
  task: any;
  onConfirm: (destinationOverrides: DestinationOverrides) => Promise<void>;
  facilities?: any[];
  facilityZones?: any[];
  warehouseZones?: any[];
  docks?: any[];
  yardSpots?: any[];
  zones?: any[];
  lockedSpots?: Set<string>;
  /**
   * The task's destination is only a zone and its facility requires a specific
   * yard spot or dock: open straight into the destination picker, with no
   * "Confirm" as-is and no Facility Zone option.
   */
  requireSpecificDestination?: boolean;
}

interface DestinationOverrides {
  destinationDockUuid?: string;
  destinationWarehouseZoneUuid?: string;
  destinationYardSpotUuid?: string;
  destinationFacilityZoneUuid?: string;
  /** Set only when the new destination is at another facility of the campus. */
  destinationFacilityId?: string;
}

type DestinationType = 'yard_spot' | 'dock' | 'facility_zone';

const MovementTaskCompletionModal: React.FC<MovementTaskCompletionModalProps> = ({
  open,
  onClose,
  task,
  onConfirm,
  facilities = [],
  facilityZones: facilityZonesProp = [],
  warehouseZones = [],
  docks: docksProp = [],
  yardSpots: yardSpotsProp = [],
  zones: zonesProp = [],
  lockedSpots: lockedSpotsProp = new Set(),
  requireSpecificDestination = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showChangeOptionsState, setShowChangeOptions] = useState(false);
  const showChangeOptions = showChangeOptionsState || requireSpecificDestination;
  const [selectedDestinationType, setSelectedDestinationType] = useState<DestinationType>('yard_spot');
  
  // Destination selection states
  const [selectedDockUuid, setSelectedDockUuid] = useState('');
  const [selectedWarehouseZoneUuid, setSelectedWarehouseZoneUuid] = useState('');
  const [selectedYardSpotUuid, setSelectedYardSpotUuid] = useState('');
  const [selectedFacilityZoneUuid, setSelectedFacilityZoneUuid] = useState('');
  const [yardSpotManualEntry, setYardSpotManualEntry] = useState('');
  const [dockManualEntry, setDockManualEntry] = useState('');
  
  // Filtering states
  const [selectedZoneFilter, setSelectedZoneFilter] = useState<string | null>(null);
  const [yardSpotFilter, setYardSpotFilter] = useState<string>('');
  const [dockFilter, setDockFilter] = useState<string>('');

  // The new destination may be anywhere in the campus of the trailer's facility.
  const sourceFacilityId = task?.source_facility_id || task?.data?.source_facility_id || task?.facility_id;
  const moveDestinations = useModalDestinations<any, any, any>({
    sourceFacilityId,
    enabled: open && showChangeOptions,
    fallback: {
      yardSpots: yardSpotsProp,
      zones: zonesProp,
      facilityZones: facilityZonesProp,
      lockedSpots: lockedSpotsProp,
    },
  });
  const { campusMode, yardSpots, zones, facilityZones, lockedSpots } = moveDestinations;
  const docks: any[] = campusMode
    ? (moveDestinations.dockDoors as ModalDockDoor[]).map(dock => ({
        uuid: dock.dockUuid,
        name: dock.door,
        warehouse_zone_uuid: dock.zoneUuid,
        zoneName: dock.zoneName,
        configuration: dock.configuration,
        trailer_name: null,
        facilityId: dock.facilityId,
      }))
    : docksProp;

  // Reset state when modal opens/closes or task changes
  useEffect(() => {
    if (!open) {
      setShowChangeOptions(false);
      setSelectedDestinationType('yard_spot');
      setSelectedDockUuid('');
      setSelectedWarehouseZoneUuid('');
      setSelectedYardSpotUuid('');
      setSelectedFacilityZoneUuid('');
      setYardSpotManualEntry('');
      setDockManualEntry('');
      setSelectedZoneFilter(null);
      setYardSpotFilter('');
      setDockFilter('');
      setError(null);
    }
  }, [open]);

  // Get original destination info - improved to handle various task data structures
  const getOriginalDestination = () => {
    if (!task) return 'Unknown destination';
    
    // Debug log to help understand task structure
    console.log('Task data for destination parsing:', task);
    console.log('Modal data - facilityZones:', facilityZones);
    console.log('Modal data - yardSpots:', yardSpots);
    console.log('Modal data - docks:', docks);
    console.log('Raw props for debugging:', {
      facilities,
      facilityZones,
      warehouseZones,
      docks,
      yardSpots
    });
    
    // PRIORITY 1: Check to_object.name (this is where "Y2-07" is in the logged data)
    if (task.to_object?.name) {
      return task.to_object.name;
    }
    
    // PRIORITY 2: Check data.assigned_yard_spot.name
    if (task.data?.assigned_yard_spot?.name) {
      return task.data.assigned_yard_spot.name;
    }
    
    // PRIORITY 3: Check for dock destinations
    if (task.to_object?.door_name || task.data?.assigned_dock?.name) {
      const dockName = task.to_object?.door_name || task.data?.assigned_dock?.name;
      return `Dock ${dockName}`;
    }
    
    // PRIORITY 4: Check from_object for reference (source location)
    if (task.from_object?.name) {
      return `From ${task.from_object.name}`;
    }
    
    // PRIORITY 5: Check other possible destination objects
    const destinationDock = task.destination_dock || task.dock || task.data?.destination_dock || task.data?.dock;
    if (destinationDock) {
      const dockName = destinationDock.name || destinationDock.door_name || destinationDock.display_name || destinationDock.uuid;
      return `Dock ${dockName}`;
    }
    
    const destinationYardSpot = task.destination_yard_spot || task.yard_spot || task.data?.destination_yard_spot || task.data?.yard_spot;
    if (destinationYardSpot) {
      const spotName = destinationYardSpot.display_name || destinationYardSpot.displayNumber || destinationYardSpot.name || destinationYardSpot.uuid;
      return spotName;
    }
    
    const destinationFacilityZone = task.destination_facility_zone || task.facility_zone || task.data?.destination_facility_zone || task.data?.facility_zone;
    if (destinationFacilityZone) {
      const zoneName = destinationFacilityZone.name || destinationFacilityZone.display_name || destinationFacilityZone.uuid;
      return `Zone ${zoneName}`;
    }
    
    // PRIORITY 6: Check string locations
    const toLocation = task.to_location || task.data?.to_location || task.destination;
    if (toLocation && typeof toLocation === 'string' && toLocation !== 'yard') {
      return toLocation;
    }
    
    return 'Original destination';
  };
  
  // Get destination type for display
  const getDestinationType = () => {
    if (!task) return 'Location';
    
    // Check task data to determine destination type
    // Prioritize to_object since it's the actual destination
    
    // Check for DOCK (has door_name property)
    if (task.to_object?.door_name || task.data?.assigned_dock?.name || task.data?.assigned_dock) {
      return 'Dock';
    }
    
    // Check for YARD SPOT (has name and uuid, not a door)
    // A yard spot will have to_object.name and possibly to_object.uuid or to_object.facilityZoneUuid
    if (task.to_object?.name && !task.to_object?.door_name) {
      return 'Yard Spot';
    }
    
    // Check for YARD SPOT in data
    if (task.data?.assigned_yard_spot?.name || task.data?.assigned_yard_spot) {
      return 'Yard Spot';
    }
    
    // Check for FACILITY ZONE (no specific spot, just a zone)
    if (task.data?.assigned_facility_zone?.name || task.data?.assigned_facility_zone) {
      return 'Facility Zone';
    }
    
    // Fallback based on movement type
    const movementType = task.movement_type || task.data?.movement_type;
    if (movementType) {
      if (movementType.includes('dock')) return 'Dock';
      if (movementType.includes('yard')) return 'Yard Spot';
      if (movementType.includes('zone')) return 'Facility Zone';
    }
    
    return 'Location';
  };

  const handleUseOriginal = async () => {
    setError(null);
    setIsLoading(true);
    
    try {
      // Complete with no destination overrides (use original)
      await onConfirm({});
      onClose();
    } catch (err) {
      console.error('Error completing task:', err);
      setError(err instanceof Error ? err.message : 'Failed to complete task');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeDestination = async () => {
    setError(null);
    
    try {
      setIsLoading(true);
      
      let destinationOverrides: DestinationOverrides = {};
      
      if (selectedDestinationType === 'dock') {
        // Handle manual entry
        let dockUuid = selectedDockUuid;
        if (!dockUuid && dockManualEntry) {
          // Find dock by name
          const matchingDock = docks.find(dock => 
            dock.name?.toLowerCase() === dockManualEntry.toLowerCase() ||
            dock.display_name?.toLowerCase() === dockManualEntry.toLowerCase() ||
            dock.door_name?.toLowerCase() === dockManualEntry.toLowerCase()
          );
          if (matchingDock) {
            dockUuid = matchingDock.uuid || matchingDock.id;
            // Auto-set warehouse zone if available
            if (matchingDock.warehouse_zone_uuid) {
              destinationOverrides.destinationWarehouseZoneUuid = matchingDock.warehouse_zone_uuid;
            }
          } else {
            setError(`Dock "${dockManualEntry}" not found`);
            return;
          }
        }
        
        if (!dockUuid) {
          setError('Please select or enter a dock');
          return;
        }
        
        destinationOverrides.destinationDockUuid = dockUuid;
        // Also set warehouse zone if it was selected
        if (selectedWarehouseZoneUuid) {
          destinationOverrides.destinationWarehouseZoneUuid = selectedWarehouseZoneUuid;
        }
      } else if (selectedDestinationType === 'yard_spot') {
        // Handle manual entry
        let yardSpotUuid = selectedYardSpotUuid;
        if (!yardSpotUuid && yardSpotManualEntry) {
          // Find yard spot by display name
          const matchingSpot = yardSpots.find(spot => 
            spot.displayNumber?.toLowerCase() === yardSpotManualEntry.toLowerCase() ||
            spot.display_name?.toLowerCase() === yardSpotManualEntry.toLowerCase()
          );
          if (matchingSpot) {
            yardSpotUuid = matchingSpot.id || matchingSpot.uuid;
          } else {
            setError(`Yard spot "${yardSpotManualEntry}" not found`);
            return;
          }
        }
        
        if (!yardSpotUuid) {
          setError('Please select or enter a yard spot');
          return;
        }
        
        destinationOverrides.destinationYardSpotUuid = yardSpotUuid;
        if (selectedFacilityZoneUuid) {
          destinationOverrides.destinationFacilityZoneUuid = selectedFacilityZoneUuid;
        }
      } else if (selectedDestinationType === 'facility_zone') {
        if (requireSpecificDestination) {
          setError('Please select a specific yard spot or dock');
          return;
        }
        if (!selectedFacilityZoneUuid) {
          setError('Please select a facility zone');
          return;
        }
        destinationOverrides.destinationFacilityZoneUuid = selectedFacilityZoneUuid;
      }
      
      // A destination at another facility of the campus moves the task there.
      const destinationFacilityId =
        selectedDestinationType === 'dock'
          ? moveDestinations.facilityOf('dock', destinationOverrides.destinationDockUuid)
          : selectedDestinationType === 'yard_spot'
            ? moveDestinations.facilityOf('yard_spot', destinationOverrides.destinationYardSpotUuid)
            : moveDestinations.facilityOf('facility_zone', destinationOverrides.destinationFacilityZoneUuid);
      if (destinationFacilityId && String(destinationFacilityId) !== String(task?.facility_id)) {
        destinationOverrides.destinationFacilityId = String(destinationFacilityId);
      }

      await onConfirm(destinationOverrides);
      onClose();
    } catch (err) {
      console.error('Error completing task:', err);
      setError(err instanceof Error ? err.message : 'Failed to complete task');
    } finally {
      setIsLoading(false);
    }
  };

  // Get available yard spots with filtering
  const getAvailableYardSpots = () => {
    return yardSpots.filter(spot => {
      const spotId = spot.id || spot.uuid;
      const hasValidDisplayNumber = (spot.displayNumber || spot.display_name) && (spot.displayNumber || spot.display_name).trim();
      const isAvailable = spot.status === 'available';
      const hasNoTrailer = !spot.trailerNumber && !spot.trailer_number;
      const isNotLocked = !lockedSpots.has(spotId);
      const matchesZone = !selectedZoneFilter || spot.zoneId === selectedZoneFilter || spot.facility_zone_uuid === selectedZoneFilter;
      const matchesSpotFilter = !yardSpotFilter.trim() || (hasValidDisplayNumber && (spot.displayNumber || spot.display_name).toLowerCase().includes(yardSpotFilter.toLowerCase()));
      return hasValidDisplayNumber && isAvailable && hasNoTrailer && isNotLocked && matchesZone && matchesSpotFilter;
    });
  };

  // Get available docks with filtering
  const getAvailableDocks = () => {
    return docks.filter(dock => {
      if (isDockUnavailableForMovement(dock.configuration)) return false;
      const hasValidName = (dock.name || dock.display_name || dock.door_name) && (dock.name || dock.display_name || dock.door_name).trim();
      const isAvailable = !dock.trailer_name;
      const matchesFilter = !dockFilter.trim() || (hasValidName && (dock.name || dock.display_name || dock.door_name).toLowerCase().includes(dockFilter.toLowerCase()));
      return hasValidName && isAvailable && matchesFilter;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Confirm Task Completion</Typography>
          <Chip
            size="small"
            label={task?.task_type?.type || 'Movement Task'}
            color="primary"
            variant="outlined"
          />
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Trailer: <strong>{task?.trailer?.trailer_number || 'Unknown'}</strong>
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!showChangeOptions ? (
          // Show current destination prominently
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Current Destination
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 'medium' }}>
                  {getDestinationType()}
                </Typography>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                  {getOriginalDestination()}
                </Typography>
              </CardContent>
            </Card>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleUseOriginal}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={16} /> : <Check />}
                sx={{ minWidth: 140 }}
              >
                {isLoading ? 'Completing...' : 'Confirm'}
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                onClick={() => setShowChangeOptions(true)}
                startIcon={<Edit />}
                sx={{ minWidth: 140 }}
              >
                Change Destination
              </Button>
            </Box>
          </Box>
        ) : (
          // Show change destination options
          <Box>
            <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current Destination:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {getDestinationType()}: {getOriginalDestination()}
              </Typography>
            </Box>
            
            {requireSpecificDestination && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This task's destination is a zone. This facility requires a specific yard spot or dock to complete it.
              </Alert>
            )}

            <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
              Select new destination:
            </Typography>
            
            <ToggleButtonGroup
              value={selectedDestinationType}
              exclusive
              onChange={(_, value) => value && setSelectedDestinationType(value)}
              fullWidth
              sx={{ mb: 3 }}
            >
              <ToggleButton value="yard_spot">
                Yard Spot
              </ToggleButton>
              <ToggleButton value="dock">
                Dock
              </ToggleButton>
              {!requireSpecificDestination && (
                <ToggleButton value="facility_zone">
                  Facility Zone
                </ToggleButton>
              )}
            </ToggleButtonGroup>

            <Box sx={{ mt: 2 }}>
              <DestinationFacilityFilter
                facilities={selectedDestinationType === 'dock' ? moveDestinations.dockFacilities : moveDestinations.facilities}
                value={moveDestinations.facilityFilter}
                onChange={value => {
                  moveDestinations.setFacilityFilter(value);
                  setSelectedDockUuid('');
                  setSelectedWarehouseZoneUuid('');
                  setSelectedYardSpotUuid('');
                  setSelectedFacilityZoneUuid('');
                  setYardSpotManualEntry('');
                  setDockManualEntry('');
                  setSelectedZoneFilter(null);
                }}
                disabled={isLoading}
              />
            </Box>

            {/* Yard Spot Selection */}
            {selectedDestinationType === 'yard_spot' && (
              <Box>
                {/* Zone filter dropdown for yard spots */}
                {zones.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Filter by Zone (Optional):
                    </Typography>
                    <Autocomplete
                      options={zones.map(zone => ({ 
                        label: zone.name, 
                        value: zone.id || zone.facility_zone_uuid,
                        color: zone.color 
                      }))}
                      value={selectedZoneFilter ? zones.find(z => (z.id || z.facility_zone_uuid) === selectedZoneFilter) ? { 
                        label: zones.find(z => (z.id || z.facility_zone_uuid) === selectedZoneFilter)!.name, 
                        value: selectedZoneFilter,
                        color: zones.find(z => (z.id || z.facility_zone_uuid) === selectedZoneFilter)!.color
                      } : null : null}
                      onChange={(_, newValue) => {
                        setSelectedZoneFilter(newValue?.value || null);
                        // Clear selected yard spot and spot filter when zone filter changes
                        setSelectedYardSpotUuid('');
                        setYardSpotManualEntry('');
                        setYardSpotFilter('');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Zone"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: option.color || '#2196F3',
                              marginRight: 1,
                            }}
                          />
                          {option.label}
                        </Box>
                      )}
                      size="small"
                    />
                  </Box>
                )}

                <TextField
                  fullWidth
                  label="Filter Yard Spots"
                  value={yardSpotFilter}
                  onChange={(e) => {
                    const filterValue = e.target.value;
                    setYardSpotFilter(filterValue);
                    
                    if (filterValue.trim()) {
                      // Get available yard spots with valid names/numbers
                      const availableSpots = yardSpots
                        .filter(spot => {
                          const spotId = spot.id || spot.uuid;
                          return spot.status === 'available' && 
                          !spot.trailerNumber && 
                          !spot.trailer_number &&
                          !lockedSpots.has(spotId) &&
                          (spot.displayNumber || spot.display_name) && 
                          (spot.displayNumber || spot.display_name).trim() &&
                          (!selectedZoneFilter || spot.zoneId === selectedZoneFilter || spot.facility_zone_uuid === selectedZoneFilter);
                        });

                      const matchingSpots = availableSpots.filter(spot =>
                        (spot.displayNumber || spot.display_name).toLowerCase().includes(filterValue.toLowerCase())
                      );

                      const exactMatch = matchingSpots.find(spot =>
                        (spot.displayNumber || spot.display_name).toLowerCase() === filterValue.toLowerCase()
                      );

                      // Auto-select if there's exactly 1 result and it's an exact match
                      if (matchingSpots.length === 1 && exactMatch) {
                        setSelectedYardSpotUuid(exactMatch.id || exactMatch.uuid);
                        setYardSpotManualEntry(exactMatch.displayNumber || exactMatch.display_name || '');
                      } else {
                        // Clear selection if multiple results or no exact match
                        setSelectedYardSpotUuid('');
                        setYardSpotManualEntry('');
                      }
                    } else {
                      // Clear selection when filter is empty
                      setSelectedYardSpotUuid('');
                      setYardSpotManualEntry('');
                    }
                  }}
                  placeholder="Type spot name to filter..."
                  helperText="Type to filter spots below (e.g. Y2-03)"
                  sx={{ mb: 2 }}
                />

                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Or select from available spots:
                </Typography>

                <div className="grid grid-cols-4 gap-2 max-h-48 overflow-auto">
                  {getAvailableYardSpots().length === 0 ? (
                    <div className="text-amber-600 p-2 border border-amber-200 rounded text-sm bg-amber-50 col-span-4">
                      {yardSpotFilter.trim() && selectedZoneFilter 
                        ? `No available yard spots found matching "${yardSpotFilter}" in the selected zone. Try clearing the filters.`
                        : yardSpotFilter.trim()
                        ? `No available yard spots found matching "${yardSpotFilter}". Try clearing the filter.`
                        : selectedZoneFilter 
                        ? `No available yard spots found in the selected zone. Try clearing the zone filter.`
                        : `No available yard spots found.`
                      }
                    </div>
                  ) : (
                    getAvailableYardSpots().map((spot) => {
                      const isSelected = selectedYardSpotUuid === (spot.id || spot.uuid);
                      // Find the zone name for this spot
                      const zone = zones.find(z => (z.id || z.facility_zone_uuid) === (spot.zoneId || spot.facility_zone_uuid));
                      
                      return (
                        <button
                          key={spot.id || spot.uuid}
                          className={`px-2 py-1 border rounded text-sm ${
                            isSelected
                              ? 'bg-blue-100 border-blue-500' 
                              : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                          onClick={() => {
                            setSelectedYardSpotUuid(spot.id || spot.uuid);
                            setYardSpotManualEntry(spot.displayNumber || spot.display_name || '');
                            setYardSpotFilter(spot.displayNumber || spot.display_name || '');
                          }}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{spot.displayNumber || spot.display_name}</span>
                            {zone && (
                              <span className="text-xs text-gray-500">{zone.name}</span>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </Box>
            )}

            {/* Dock Selection */}
            {selectedDestinationType === 'dock' && (
              <Box>
                <TextField
                  fullWidth
                  label="Filter Dock Doors"
                  value={dockFilter}
                  onChange={(e) => {
                    const filterValue = e.target.value;
                    setDockFilter(filterValue);
                    
                    if (filterValue.trim()) {
                      // Get available dock doors with valid names/numbers
                      const availableDoors = docks
                        .filter(door => 
                          !door.trailer_name && 
                          (door.name || door.display_name || door.door_name) && 
                          (door.name || door.display_name || door.door_name).trim()
                        );

                      const matchingDoors = availableDoors.filter(door => {
                        const doorName = door.name || door.display_name || door.door_name || '';
                        return doorName.toLowerCase().includes(filterValue.toLowerCase());
                      });

                      const exactMatch = matchingDoors.find(door => {
                        const doorName = door.name || door.display_name || door.door_name || '';
                        return doorName.toLowerCase() === filterValue.toLowerCase();
                      });

                      // Auto-select if there's exactly 1 result and it's an exact match
                      if (matchingDoors.length === 1 && exactMatch) {
                        setSelectedDockUuid(exactMatch.uuid || exactMatch.id);
                        setDockManualEntry(exactMatch.name || exactMatch.display_name || exactMatch.door_name || '');
                        // Auto-set the warehouse zone if available
                        if (exactMatch.warehouse_zone_uuid) {
                          setSelectedWarehouseZoneUuid(exactMatch.warehouse_zone_uuid);
                        }
                      } else {
                        // Clear selection if multiple results or no exact match
                        setSelectedDockUuid('');
                        setDockManualEntry('');
                      }
                    } else {
                      // Clear selection when filter is empty
                      setSelectedDockUuid('');
                      setDockManualEntry('');
                    }
                  }}
                  placeholder="Type dock door name to filter..."
                  helperText="Type to filter dock doors below (e.g. D-01)"
                  sx={{ mb: 2 }}
                />

                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Or select from available docks:
                </Typography>

                <div className="grid grid-cols-4 gap-2 max-h-48 overflow-auto">
                  {getAvailableDocks().length === 0 ? (
                    <div className="text-amber-600 p-2 border border-amber-200 rounded text-sm bg-amber-50 col-span-4">
                      {dockFilter.trim() 
                        ? `No dock doors found matching "${dockFilter}". Try clearing the filter.`
                        : `No available dock doors found.`
                      }
                    </div>
                  ) : (
                    getAvailableDocks().map((dock) => {
                      const isSelected = selectedDockUuid === (dock.uuid || dock.id);
                      return (
                        <button
                          key={dock.uuid || dock.id}
                          className={`px-2 py-1 border rounded text-sm ${
                            isSelected
                              ? 'bg-blue-100 border-blue-500' 
                              : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                          onClick={() => {
                            setSelectedDockUuid(dock.uuid || dock.id);
                            setDockManualEntry(dock.name || dock.display_name || dock.door_name || '');
                            setDockFilter(dock.name || dock.display_name || dock.door_name || '');
                            // Auto-set the warehouse zone if available
                            if (dock.warehouse_zone_uuid) {
                              setSelectedWarehouseZoneUuid(dock.warehouse_zone_uuid);
                            }
                          }}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{dock.name || dock.display_name || dock.door_name}</span>
                            {(dock.warehouse_zone_name || dock.warehouse_zone_uuid) && (
                              <span className="text-xs text-gray-500">
                                {dock.warehouse_zone_name || 
                                 warehouseZones?.find(z => 
                                  (z.zoneUuid || z.uuid || z.id) === dock.warehouse_zone_uuid
                                )?.zoneName || 
                                warehouseZones?.find(z => 
                                  (z.zoneUuid || z.uuid || z.id) === dock.warehouse_zone_uuid
                                )?.name || 
                                'Warehouse Zone'}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </Box>
            )}

            {/* Facility Zone Selection */}
            {selectedDestinationType === 'facility_zone' && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Select from available facility zones:
                </Typography>

                <div className="grid grid-cols-4 gap-2 max-h-48 overflow-auto">
                  {facilityZones.length === 0 ? (
                    <div className="text-amber-600 p-2 border border-amber-200 rounded text-sm bg-amber-50 col-span-4">
                      No facility zones found.
                    </div>
                  ) : (
                    facilityZones.map((zone, index) => {
                      const zoneId = zone.facility_zone_uuid || zone.uuid || zone.id || `zone-${index}`;
                      const zoneName = zone.facility_zone_name || zone.name || 'Unnamed Zone';
                      const isSelected = selectedFacilityZoneUuid === zoneId;
                      return (
                        <button
                          key={zoneId}
                          className={`px-2 py-1 border rounded text-sm ${
                            isSelected
                              ? 'bg-blue-100 border-blue-500' 
                              : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                          onClick={() => setSelectedFacilityZoneUuid(zoneId)}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{zoneName}</span>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        {showChangeOptions && (
          <>
            {!requireSpecificDestination && (
              <Button onClick={() => setShowChangeOptions(false)} disabled={isLoading}>
                Back
              </Button>
            )}
            <Button
              onClick={handleChangeDestination}
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} /> : <Check />}
            >
              {isLoading ? 'Completing...' : 'Complete Task'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MovementTaskCompletionModal;